<template>
    <v-card>
        <v-card-title>
            <v-layout row wrap>
                <v-col grow class="ma-2"> Task Templates </v-col>
                <v-btn color="primary" class="ma-2" @click="showCreateExperimentGroupTaskTemplateForm">
                    New Task Template
                </v-btn>
            </v-layout>
        </v-card-title>
        <v-data-table
            :headers="mergedTaskTemplateHeaders"
            :items="mergedTaskTemplateListItems"
            no-results-text="No tasks."
            item-key="id"
            class="elevation-1"
            :items-per-page="25"
        >
            <template v-slot:item.isSchemaConfigTaskTemplate="{ item }">
                <v-icon>{{ !isExperimentGroupTaskTemplate(item.baseTaskTemplate) ? "$check" : "" }}</v-icon>
            </template>
            <template v-slot:item.edit="{ item }">
                <v-icon v-if="isExperimentGroupTaskTemplate(item.baseTaskTemplate)" @click="showEditExperimentGroupTaskTemplateForm(item)">$pencil</v-icon>
            </template>
            <template v-slot:item.delete="{ item }">
                <v-icon v-if="isExperimentGroupTaskTemplate(item.baseTaskTemplate)" @click="deleteExperimentGroupTaskTemplate(item as SavedExperimentGroupTaskTemplate)"
                >$delete</v-icon
                >
            </template>
        </v-data-table>
        <v-dialog v-model="showExperimentGroupTaskTemplateFormDialog" width="1000" persistent>
            <experiment-group-task-template-form
                v-if="showExperimentGroupTaskTemplateFormDialog"
                :readout-groups="readoutGroups"
                :task-groups="taskGroups"
                :experiment-group-task-template="selectedExperimentGroupTaskTemplate ?? null"
                :experimentGroup="experimentGroup"
                @closed="experimentGroupTaskTemplateFormDialogClosed"
                @saved="savedExperimentGroupTaskTemplate"
            />
        </v-dialog>
    </v-card>
</template>
<script setup lang="ts">
import { ref, computed } from "vue";
import { SavedTaskGroup } from "@/services/taskGroups/taskGroupsAPI";
import { SavedReadoutGroup } from "@/services/readoutGroups/readoutGroupsAPI";
import {
    BaseTaskTemplate,
    SavedBaseTaskTemplate, SavedExperimentGroupTaskTemplate, taskTemplatesAPI,
} from "@/services/taskTemplates/taskTemplatesAPI";
import { findReadoutGroupById, findTaskGroupById } from "@/lib/taskUtils";
import _ from "lodash";
import { withLoadingScreen } from "@/lib/loading";
import { VDataTableHeader } from "@/vuetifyTypeFixes";
import { SavedExperimentGroup } from "@/services/experimentGroups/experimentGroupsAPI";
import ExperimentGroupTaskTemplateForm from "@/components/ExperimentGroups/ExperimentGroupTaskTemplateForm.vue";

const { experimentGroup, mergedTaskTemplates, taskGroups, readoutGroups } = defineProps<{
    experimentGroup: SavedExperimentGroup,
    mergedTaskTemplates: SavedBaseTaskTemplate[],
    taskGroups: SavedTaskGroup[],
    readoutGroups: SavedReadoutGroup[],
}>();

const emit = defineEmits<{
    (e: "saved");
    (e: "deleted");
}>()

function isExperimentGroupTaskTemplate(baseTaskTemplate: BaseTaskTemplate): boolean {
    return !_.isNil(baseTaskTemplate.experimentGroupId) && _.isNil(baseTaskTemplate.schemaConfigId);
}


export type ExperimentGroupTaskTemplateListItem = SavedBaseTaskTemplate & {
    baseTaskTemplate: SavedBaseTaskTemplate,
    taskGroupName: string,
    readoutGroupName: string,
}

const showExperimentGroupTaskTemplateFormDialog = ref<boolean>(false);
const selectedExperimentGroupTaskTemplate = ref<SavedExperimentGroupTaskTemplate>();
const mergedTaskTemplateListItems = computed(() => {
    return mapMergedTaskTemplatesToListItems(mergedTaskTemplates);
})

const mergedTaskTemplateHeaders = ref<VDataTableHeader[]>([
    {
        title: "Day",
        value: "day",
        align: "start",
        sortable: true,
        width: "5%",
    },
    {
        title: "Task Group",
        value: "taskGroupName",
        sortable: true,
        align: "start",
        width: "30%",
    },
    {
        title: "Readout Group",
        value: "readoutGroupName",
        sortable: true,
        align: "start",
        width: "50%",
    },
    {
        title: "Schema Config Task Template",
        value: "isSchemaConfigTaskTemplate",
        align: "start",
        width: "5%",
    },
    {
        title: "Edit",
        value: "edit",
        align: "start",
        width: "5%",
    },
    {
        title: "Delete",
        value: "delete",
        align: "start",
        width: "5%",
    },
]);

function mapMergedTaskTemplatesToListItems(mergedTaskTemplates: SavedBaseTaskTemplate[]): ExperimentGroupTaskTemplateListItem[] {
    return mergedTaskTemplates.map((baseTaskTemplate: SavedBaseTaskTemplate) => {

        const taskGroup = findTaskGroupById(baseTaskTemplate.taskGroupId, taskGroups);

        if (_.isNil(taskGroup)) {
            return {
                ...baseTaskTemplate,
                day: -1,
                baseTaskTemplate,
                taskGroupName: "Error: missing Task Group",
                readoutGroupName: "",
            };
        }

        const readoutGroup = findReadoutGroupById(
            baseTaskTemplate.readoutGroupId,
            readoutGroups
        );

        const day = _.isNil(baseTaskTemplate.day)
            ? taskGroup.day
            : baseTaskTemplate.day;

        if (_.isNil(readoutGroup)) {
            return {
                ...baseTaskTemplate,
                day,
                baseTaskTemplate,
                taskGroupName: taskGroup.name,
                readoutGroupName: "Error: missing Readout Group",
            };
        }

        return {
            ...baseTaskTemplate,
            day,
            baseTaskTemplate,
            taskGroupName: taskGroup.name,
            readoutGroupName: readoutGroup.name,
        };
    });
}

function showCreateExperimentGroupTaskTemplateForm() {
    showExperimentGroupTaskTemplateFormDialog.value = true;
    selectedExperimentGroupTaskTemplate.value = undefined;
}

function showEditExperimentGroupTaskTemplateForm(item: ExperimentGroupTaskTemplateListItem) {
    selectedExperimentGroupTaskTemplate.value = item.baseTaskTemplate as SavedExperimentGroupTaskTemplate;
    showExperimentGroupTaskTemplateFormDialog.value = true;
}

function experimentGroupTaskTemplateFormDialogClosed() {
    showExperimentGroupTaskTemplateFormDialog.value = false;
    selectedExperimentGroupTaskTemplate.value = undefined;
}

function savedExperimentGroupTaskTemplate() {
    selectedExperimentGroupTaskTemplate.value = undefined;
    showExperimentGroupTaskTemplateFormDialog.value = false;
    emit("saved");
}

async function deleteExperimentGroupTaskTemplate(item: SavedExperimentGroupTaskTemplate) {
    if (confirm("Are you sure you want to delete this task?")) {
        return withLoadingScreen(async () => {
            await taskTemplatesAPI.byExperimentGroup(experimentGroup.id).delete(item.id);
            emit("deleted");
        });
    }
}

</script>
